/**
 * Compute SHA-256 checksum of case input and output data.
 * Must match the backend Go implementation (pkg/checksum/checksum.go)
 * and Python implementation (bvagent) for consistent drift detection.
 *
 * Algorithm: JSON.stringify with sorted keys → SHA-256 → hex string
 */

/**
 * Serialize a value to JSON matching Python's json.dumps(sort_keys=True, default=str).
 * Uses ", " after commas and ": " after colons (Python defaults), with sorted keys.
 */
function pythonJsonDumps(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    const items = value.map(pythonJsonDumps);
    return `[${items.join(', ')}]`;
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    const pairs = keys.map(k => `${JSON.stringify(k)}: ${pythonJsonDumps(obj[k])}`);
    return `{${pairs.join(', ')}}`;
  }
  // Fallback: match Python's default=str
  return JSON.stringify(String(value));
}

/**
 * Compute SHA-256 hex digest of the combined input + output data.
 * Matches the bvagent Python checksum stored on the Summary model.
 */
export async function computeInputOutputChecksum(
  inputData: Record<string, unknown> | undefined,
  outputData: Record<string, unknown> | undefined,
): Promise<string> {
  const combined: Record<string, unknown> = {
    input: inputData || {},
    output: outputData || {},
  };

  const jsonStr = pythonJsonDumps(combined);
  const encoded = new TextEncoder().encode(jsonStr);
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hash;
}
